import React from "react";
import { IRenderTableV3Props } from "./RenderTableV3.types";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  SxProps,
  TablePagination,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  IDataTableV2DetailColumn,
  IDataTableV2DetailRowData,
  IDataTableV2FormattedData,
  IDataTableV2GroupBy,
  IDataTableV2MasterColumn,
  IDataTableV2MasterColumnResize,
  IDataTableV2Props,
} from "../../interfaces/IDataTableV2Props";
import "../../TableV2.css";
import { useDataTableV2Context } from "../../context/DataTableV2Provider";
import { TablePaginationActions } from "../../../Table/TablePaginationActions";
import { getUniqueId } from "../../../../helpers";
import { DataTableV2SelectAll } from "../DataTableV2SelectAll/DataTableV2SelectAll";
import { CustomCheckbox } from "../../../formsComponents";
import { IDataTableV2SelectAllCheckedType } from "../DataTableV2SelectAll/interfaces/IDataTableV2SelectAllProps";
import { DeleteOutline } from "@mui/icons-material";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import produce from "immer";
import "./drag.css";
import { TopbarHeight } from "../../../../assets/global/Theme-variable";

const removePx = (value: string) =>
  typeof value === "string" ? parseInt(value.replace("px", ""), 10) : value;

export const RenderTableV2: React.FC<IRenderTableV3Props> = (props) => {
  const {
    masterColumns,
    detailColumns,
    rows,
    isGroupBy,
    onRenderMasterColumnHeader,
    isDataLoading,
    isPagination,
    loaderSkeletonRows,
    extraFetchFactor,
    selectionMode,
    totalRecords,
    onTableMasterRowCellRender,
    onTableDetailRowCellRender,

    groupBy,
    uniqueRowKeyName,
    selectType,
    selectedRows,
    onSelectRow,
    onSelectionAllRows,
    masterColumnResize,
    pageNumber = 1,
    rowsPerPageOptions: rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    rowHighlightCondition,
    autoColumnWidths = {},
    stickyHeader,
  } = props;
  const { tableConfig = {} } = useDataTableV2Context();

  // Prop takes precedence over the DataTableV2Provider tableConfig context.
  const effectiveStickyHeader = stickyHeader ?? tableConfig.stickyHeader;

  const hasDetailColumns = !!(detailColumns && detailColumns.length > 0);

  // Track the CardContent container width so we can enforce minWidth per column
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ─── Sticky bottom horizontal scrollbar (option B) ─────────────────────────
  // A slim scrollbar strip pinned to the bottom of the viewport, synced with the
  // table's real horizontal scroll (CardContent). Only active when the header is
  // sticky AND the table actually overflows horizontally.
  const stickyScrollbarRef = React.useRef<HTMLDivElement>(null);
  // The detached sticky-header inner row — translated horizontally to track the
  // body's horizontal scroll (the header lives OUTSIDE the horizontal scroller so
  // its position:sticky can bind to the page, not the scroll container).
  const stickyHeaderInnerRef = React.useRef<HTMLDivElement>(null);
  // Full scrollable width of the table content (drives the strip's inner spacer).
  const [scrollContentWidth, setScrollContentWidth] = React.useState(0);
  // Whether the table overflows horizontally (controls strip visibility).
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = React.useState(false);

  // Keep the strip's spacer width + overflow flag in sync with the table size.
  React.useEffect(() => {
    if (!effectiveStickyHeader) return;
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      setScrollContentWidth(el.scrollWidth);
      setHasHorizontalOverflow(el.scrollWidth > el.clientWidth + 1);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // Re-measure when structural inputs change (columns/rows/resize).
  }, [effectiveStickyHeader, masterColumns, rows, autoColumnWidths, containerWidth]);

  // Two-way scroll sync between the real scroller (CardContent) and the strip.
  React.useEffect(() => {
    if (!effectiveStickyHeader) return;
    const scroller = containerRef.current;
    const strip = stickyScrollbarRef.current;
    if (!scroller || !strip) return;

    let lock = false;
    const syncHeader = (x: number) => {
      const headerInner = stickyHeaderInnerRef.current;
      if (!headerInner) return;
      // Slide the whole header row to track horizontal scroll
      headerInner.style.transform = `translateX(${-x}px)`;
      // Counter-translate any first/last sticky column cells so they stay
      // pinned at their edge. The parent row moves by -x, so these cells
      // must move by +x to remain visually at left:0 / right:0.
      const stickyFirstCells = headerInner.querySelectorAll<HTMLElement>(
        "[data-sticky-first]"
      );
      stickyFirstCells.forEach((cell) => {
        cell.style.transform = `translateX(${x}px)`;
      });
      const stickyLastCells = headerInner.querySelectorAll<HTMLElement>(
        "[data-sticky-last]"
      );
      stickyLastCells.forEach((cell) => {
        // For right-sticky: position from right edge stays constant
        cell.style.transform = `translateX(${x}px)`;
      });
    };
    const onScroller = () => {
      if (lock) return;
      lock = true;
      strip.scrollLeft = scroller.scrollLeft;
      syncHeader(scroller.scrollLeft);
      lock = false;
    };
    const onStrip = () => {
      if (lock) return;
      lock = true;
      scroller.scrollLeft = strip.scrollLeft;
      syncHeader(strip.scrollLeft);
      lock = false;
    };

    // Initialise header offset to the current scroll position.
    syncHeader(scroller.scrollLeft);

    scroller.addEventListener("scroll", onScroller, { passive: true });
    strip.addEventListener("scroll", onStrip, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroller);
      strip.removeEventListener("scroll", onStrip);
    };
  }, [effectiveStickyHeader, hasHorizontalOverflow]);

  // Total column count including selection/detail expander columns
  const extraColCount =
    (hasDetailColumns ? 1 : 0) + (selectionMode !== "none" ? 1 : 0);
  const totalColCount = masterColumns.length + extraColCount;

  // Each column must be at least this wide so total ≥ container width
  const minColWidth = totalColCount > 0 && containerWidth > 0
    ? Math.max(60, Math.floor(containerWidth / totalColCount))
    : 60;

  const currentPageItems =
    !totalRecords && rowsPerPage > 0 && isPagination && !extraFetchFactor
      ? rows.slice(
        (pageNumber - 1) * rowsPerPage,
        (pageNumber - 1) * rowsPerPage + rowsPerPage,
      )
      : rows;
  const totalPages =
    totalRecords !== undefined
      ? Math.ceil(totalRecords / rowsPerPage)
      : Math.ceil(rows.length / rowsPerPage);
  const handlePageChange = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const rowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(
        1,
        rowsPerPage === -1 ? totalRecords || rows.length : rowsPerPage,
      );
    }
  };

  return (
    <Card
      className="tableScroll1"
      sx={{
        borderRadius: {
          md: "10px",
          lg: "15px",
          xl: "20px",
        },
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 10px",
        border: 0,
        borderTopLeftRadius: {
          md: 0,
          lg: 0,
          xl: 0,
        },
        borderTopRightRadius: {
          md: 0,
          lg: 0,
          xl: 0,
        },
        padding: 0,
        marginTop: 0,

        m: 0,
        marginBottom: 3,
        // overflow must be visible so the sticky detached header (sticky mode)
        // can escape the card boundary, and so containerRef drives all scrolling.
        overflow: "visible",
      }}
    >
      {(selectedRows.length > 0 || selectType === "AllPages") && (
        <Box
          sx={{
            padding: {
              md: 1.3,
              lg: 2,
              xl: 2,
            },
            borderRadius: 1,
            m: {
              md: 1,
              lg: 1,
              xl: 1,
            },
            mt: {
              md: 1,
              lg: 1,
              xl: 1,
            },
            backgroundColor: "primary.light",
          }}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Typography
              variant="body1"
              fontWeight={"bold"}
              color="primary.main"
            >
              {`${selectType === "AllPages" ? totalRecords : selectedRows.length
                } Row's Selected`}
            </Typography>
            <DeleteOutline
              sx={{ ml: 1, cursor: "pointer" }}
              fontSize="small"
              color="primary"
              onClick={() => onSelectionAllRows(false)}
            />
          </Box>
        </Box>
      )}
      <CardContent
        sx={{
          padding: "0px !important",
          // overflow must be visible so it never becomes a scroll container.
          // All horizontal scrolling is handled by containerRef below, which
          // contains both the header row (non-sticky mode) and the body rows.
          // In sticky mode this also allows the detached header Box to use
          // position:sticky relative to the page, not this element.
          overflow: "visible",
        }}
      >
        <Box
          position={"relative"}
          sx={{
            position: "relative",
            width: "100%",
            minWidth: "100%",
          }}
        >
          {/* ── Detached sticky header (stickyHeader mode only) ─────────────────
              Lives OUTSIDE the horizontal scroller so position:sticky binds to
              the page viewport (vertical pin). Its inner row is translated
              horizontally via JS to track the body's horizontal scroll.
              In normal (non-sticky) mode the header is rendered INSIDE
              containerRef instead (see below) so it shares the same scroll
              container as the rows — this is required for position:sticky on
              individual cells (isFirstColumnSticky) to work correctly. */}
          {effectiveStickyHeader && (
          <Box
            sx={{
              position: "sticky",
              top: TopbarHeight - 9,
              zIndex: 5,
              width: "100%",
              overflow: "hidden",
            }}
          >
          <Stack
            ref={stickyHeaderInnerRef}
            direction={"row"}
            spacing={0}
            sx={{
              position: "relative",
              // Match the body's rendered width so columns (incl. the flexing
              // last column) align exactly with the rows below.
              width:
                hasHorizontalOverflow && scrollContentWidth > 0
                  ? `${scrollContentWidth}px`
                  : "100%",
              minWidth: "100%",
              willChange: "transform",
            }}
          >
            {hasDetailColumns && (
              <DataTableV2Cell
                renderColumnIn="header"
                width="80px"
                disableResize
                minColumnWidth={minColWidth}
                isDetachedHeader
              />
            )}
            {selectionMode === "multiple" && (
              <DataTableV2Cell
                renderColumnIn="header"
                width="80px"
                masterColumnResize={masterColumnResize}
                disableResize
                minColumnWidth={minColWidth}
                isDetachedHeader
              >
                <DataTableV2SelectAll
                  checkedType={selectType}
                  onChange={onSelectionAllRows}
                />
              </DataTableV2Cell>
            )}
            {selectionMode === "single" && (
              <DataTableV2Cell
                renderColumnIn="header"
                width="80px"
                masterColumnResize={masterColumnResize}
                disableResize
                minColumnWidth={minColWidth}
                isDetachedHeader
              />
            )}
            {masterColumns.map((column, index) => {
              if (!column) return null;
              const isLast = index === masterColumns.length - 1;
              return (
                <DataTableV2Cell
                  key={column.key}
                  renderColumnIn="header"
                  width={column.width}
                  masterColumnResize={masterColumnResize}
                  isFirstColumnSticky={column.isFirstColumnSticky}
                  isLastColumnSticky={column.isLastColumnSticky}
                  masterColumnKeyName={column.key}
                  disableResize={column.disableResize}
                  minColumnWidth={minColWidth}
                  columnMinWidth={column.minWidth}
                  isLastColumn={isLast}
                  autoColumnWidth={autoColumnWidths[column.key]}
                  isDetachedHeader
                  stickyMarker={
                    column.isFirstColumnSticky
                      ? "first"
                      : column.isLastColumnSticky
                      ? "last"
                      : undefined
                  }
                >
                  {onRenderMasterColumnHeader(column)}
                </DataTableV2Cell>
              );
            })}
          </Stack>
          </Box>
          )}

          {/* ── Scroll container: rows + header (non-sticky) or rows-only (sticky) ──
              In non-sticky mode this box contains BOTH the header row and the body
              rows so they share one scroll container. That lets position:sticky on
              isFirstColumnSticky/isLastColumnSticky header cells work correctly.
              In sticky mode only body rows live here; the header is the detached
              box above, synced via translateX. */}
          <Box
            ref={containerRef}
            sx={{
              width: "100%",
              minWidth: "100%",
              overflowX: "auto",
              overflowY: "visible",
              // Hide native scrollbar in sticky mode — the pinned bottom strip
              // provides it. In normal mode keep the native scrollbar visible.
              ...(effectiveStickyHeader
                ? {
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }
                : {}),
            }}
          >
          {/* ── Inline header (non-sticky mode only) ──────────────────────────
              Rendered inside the scroll container so it scrolls in sync with
              the rows. position:sticky on individual cells (isFirstColumnSticky)
              binds to THIS container and pins correctly during horizontal scroll. */}
          {!effectiveStickyHeader && (
            <Stack
              direction={"row"}
              spacing={0}
              sx={{ position: "relative", width: "100%", minWidth: "100%" }}
            >
              {hasDetailColumns && (
                <DataTableV2Cell
                  renderColumnIn="header"
                  width="80px"
                  disableResize
                  minColumnWidth={minColWidth}
                />
              )}
              {selectionMode === "multiple" && (
                <DataTableV2Cell
                  renderColumnIn="header"
                  width="80px"
                  masterColumnResize={masterColumnResize}
                  disableResize
                  minColumnWidth={minColWidth}
                >
                  <DataTableV2SelectAll
                    checkedType={selectType}
                    onChange={onSelectionAllRows}
                  />
                </DataTableV2Cell>
              )}
              {selectionMode === "single" && (
                <DataTableV2Cell
                  renderColumnIn="header"
                  width="80px"
                  masterColumnResize={masterColumnResize}
                  disableResize
                  minColumnWidth={minColWidth}
                />
              )}
              {masterColumns.map((column, index) => {
                if (!column) return null;
                const isLast = index === masterColumns.length - 1;
                return (
                  <DataTableV2Cell
                    key={column.key}
                    renderColumnIn="header"
                    width={column.width}
                    masterColumnResize={masterColumnResize}
                    isFirstColumnSticky={column.isFirstColumnSticky}
                    isLastColumnSticky={column.isLastColumnSticky}
                    masterColumnKeyName={column.key}
                    disableResize={column.disableResize}
                    minColumnWidth={minColWidth}
                    columnMinWidth={column.minWidth}
                    isLastColumn={isLast}
                    autoColumnWidth={autoColumnWidths[column.key]}
                  >
                    {onRenderMasterColumnHeader(column)}
                  </DataTableV2Cell>
                );
              })}
            </Stack>
          )}
          <Stack>
            {/* { Array.from(
              Array(loaderSkeletonRows).keys().map(currentIndex=>{
          return(
            <Stack>
              {masterColumns.map((column)=>{
                return<DataTableV2Cell key={column.key} renderColumnIn="row" width={column.width}> <Skeleton sx={{height: "100%", width: "100%"}} /></DataTableV2Cell>
              })

              }
            </>
          )
              })

            }
            </>
            )} */}
            {isDataLoading && (
              <>
                {Array.from(Array(loaderSkeletonRows)).map((currentIndex) => {
                  return (
                    <Stack>
                      <Stack direction={"row"} spacing={0}>
                        {hasDetailColumns && (
                          <DataTableV2Cell
                            renderColumnIn="row"
                            width={"80px"}
                            masterColumnResize={masterColumnResize}
                            disableResize
                            minColumnWidth={minColWidth}
                          >
                            <Skeleton sx={{ width: "100%" }} />
                          </DataTableV2Cell>
                        )}
                        {masterColumns.map((column, index) => {
                          if (!column) return null;
                          return (
                            <DataTableV2Cell
                              key={column.key}
                              renderColumnIn="row"
                              width={column.width}
                              masterColumnResize={masterColumnResize}
                              masterColumnKeyName={column.key}
                              disableResize={column.disableResize}
                              minColumnWidth={minColWidth}
                            >
                              <Skeleton sx={{ width: "100%" }} height={20} />
                            </DataTableV2Cell>
                          );
                        })}
                      </Stack>
                    </Stack>
                  );
                })}
              </>
            )}

            {!isDataLoading &&
              (extraFetchFactor
                ? currentPageItems.slice(0, rowsPerPage)
                : currentPageItems
              ).map((row, index) => {
                const columnsIndexes = Array.from(
                  Array(masterColumns.length).keys(),
                );
                return (
                  <>
                    <Row
                      key={
                        uniqueRowKeyName ? row[uniqueRowKeyName] : getUniqueId()
                      }
                      masterColumns={masterColumns}
                      detailColumns={detailColumns || []}
                      hasDetailColumns={hasDetailColumns}
                      index={index}
                      groupBy={groupBy}
                      isGroupBy={isGroupBy}
                      masterColumnIndexes={columnsIndexes}
                      onTableMasterRowCellRender={onTableMasterRowCellRender}
                      onTableDetailRowCellRender={onTableDetailRowCellRender}
                      row={row}
                      selectionMode={selectionMode}
                      selectType={selectType}
                      selectedRows={selectedRows}
                      uniqueRowKeyName={uniqueRowKeyName}
                      onSelectRow={onSelectRow}
                      masterColumnResize={masterColumnResize}
                      minColWidth={minColWidth}
                      rowHighlightCondition={rowHighlightCondition}
                      autoColumnWidths={autoColumnWidths}
                    />
                  </>
                );
              })}
          </Stack>
          </Box>
        </Box>
      </CardContent>

      {/* Sticky bottom horizontal scrollbar — pinned to the viewport bottom and
          synced with the table's horizontal scroll. Only shown when the header is
          sticky and the table overflows horizontally. */}
      {effectiveStickyHeader && hasHorizontalOverflow && (
        <Box
          ref={stickyScrollbarRef}
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 6,
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            // Only the scrollbar is visible; no content height.
            "&::-webkit-scrollbar": { height: "10px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#7f7f7f",
              borderRadius: "10px",
            },
            backgroundColor: "background.paper",
          }}
        >
          {/* Spacer matching the table's full scroll width to produce the scrollbar */}
          <Box sx={{ width: `${scrollContentWidth}px`, height: "1px" }} />
        </Box>
      )}

      {isPagination && (
        <Stack>
          <Stack direction={"row"} justifyContent={"start"}>
            <TablePagination
              rowsPerPageOptions={props.paginationList}
              sx={{
                borderBottom: 0,
              }}
              count={totalRecords || rows.length}
              rowsPerPage={rowsPerPage}
              page={pageNumber - 1}
              SelectProps={{
                native: true,
              }}
              labelDisplayedRows={({ from, to }) => {
                if (extraFetchFactor) {
                  const count = rows.length - extraFetchFactor;
                  if (count < -1) {
                    return "Loading...";
                  }
                  if (rows.length === extraFetchFactor) {
                    return `${rows.length} rows currently shown`;
                  }
                  if (rows.length <= rowsPerPage) {
                    return `${rows.length} rows currently shown`;
                  }
                  return `${rows.length - extraFetchFactor
                    } rows currently shown`; // Show range only
                }
                return `${from}-${to} of ${totalRecords || rows.length}`; // Include total when available
              }}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              ActionsComponent={(props) => (
                <TablePaginationActions
                  {...props}
                  extraFetchFactor={extraFetchFactor}
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  pageNumber={pageNumber - 1}
                  totalPages={totalPages}
                />
              )}
              nextIconButtonProps={{
                disabled: extraFetchFactor
                  ? rows.length < rowsPerPage
                  : pageNumber >= totalPages,
              }}
            />
          </Stack>
        </Stack>
      )}
    </Card>
  );
};

const Row: React.FC<{
  hasDetailColumns: boolean;
  masterColumns: IDataTableV2MasterColumn[];
  detailColumns: IDataTableV2DetailColumn[];
  masterColumnIndexes: number[];
  index: number;
  isGroupBy: boolean;
  groupBy?: IDataTableV2GroupBy;
  row: IDataTableV2FormattedData;
  onTableMasterRowCellRender: (
    row: IDataTableV2FormattedData,
    columnIndex: number,
    rowIndex: number,
  ) => React.ReactNode;
  onTableDetailRowCellRender: (
    row: IDataTableV2DetailRowData,
    columnIndex: number,
    rowIndex: number,
  ) => React.ReactNode;
  selectionMode: IDataTableV2Props["selectionMode"];
  selectType: IDataTableV2SelectAllCheckedType;
  uniqueRowKeyName: string;
  selectedRows: IDataTableV2FormattedData[];
  onSelectRow: (
    row: IDataTableV2FormattedData,
  ) => (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  masterColumnResize?: IDataTableV2MasterColumnResize;
  minColWidth?: number;
  rowHighlightCondition?: (row: any) => string | undefined;
  autoColumnWidths?: Record<string, number>;
}> = (props) => {
  const {
    hasDetailColumns,
    masterColumns,
    detailColumns,
    masterColumnIndexes,
    row,
    groupBy,
    onTableMasterRowCellRender,
    onTableDetailRowCellRender,
    index,
    isGroupBy,
    selectionMode,
    selectType,
    selectedRows,
    uniqueRowKeyName,
    onSelectRow,
    masterColumnResize,
    minColWidth = 60,
    rowHighlightCondition,
    autoColumnWidths = {},
  } = props;
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const highlightColor = rowHighlightCondition?.(row);

  return (
    <React.Fragment>
      <Stack direction={"row"} spacing={0} sx={{ width: "100%", backgroundColor: highlightColor || "transparent" }}>
        {hasDetailColumns && (
          <DataTableV2Cell renderColumnIn="row" width="80px" minColumnWidth={minColWidth} rowHighlightColor={highlightColor}>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </DataTableV2Cell>
        )}
        {selectionMode !== "none" && (
          <DataTableV2Cell
            renderColumnIn="row"
            width="80px"
            masterColumnResize={masterColumnResize}
            minColumnWidth={minColWidth}
            rowHighlightColor={highlightColor}
          >
            <CustomCheckbox
              disabled={selectType === "AllPages"}
              checked={
                selectedRows.findIndex(
                  (x: any) => x[uniqueRowKeyName] === row[uniqueRowKeyName],
                ) > -1 || selectType === "AllPages"
              }
              onChange={onSelectRow(row)}
            />
          </DataTableV2Cell>
        )}

        {masterColumnIndexes.map((columnIndex: any) => {
          const column: IDataTableV2MasterColumn = masterColumns[columnIndex];
          if (!column) return null;
          const detailColumnIndex = detailColumns.findIndex(
            (x) => x.fieldName === column.fieldName,
          );
          const isLast = columnIndex === masterColumns.length - 1;

          return (
            <DataTableV2Cell
              renderColumnIn="row"
              width={column?.width}
              masterColumnResize={masterColumnResize}
              isFirstColumnSticky={column?.isFirstColumnSticky}
              isLastColumnSticky={column?.isLastColumnSticky}
              masterColumnKeyName={column?.key}
              disableResize={column.disableResize}
              minColumnWidth={minColWidth}
              columnMinWidth={column.minWidth}
              isLastColumn={isLast}
              rowHighlightColor={highlightColor}
              autoColumnWidth={autoColumnWidths[column.key]}
            >
              {isGroupBy && detailColumnIndex > -1 ? null : (
                <>
                  {onTableMasterRowCellRender(row, columnIndex, index)}
                  {groupBy && groupBy.columName === column.fieldName ? (
                    <Typography
                      component={"span"}
                      ml={1}
                      fontWeight={600}
                    >{`(${row.childs.length})`}</Typography>
                  ) : null}
                </>
              )}
            </DataTableV2Cell>
          );
        })}
      </Stack>
      {hasDetailColumns && (
        <Stack>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <>
              {row.childs.length === 0 && (
                <Stack
                  width={"100%"}
                  direction={"row"}
                  p={3}
                  justifyContent={"center"}
                >
                  <Typography
                    variant="body1"
                    fontSize={"1.1rem"}
                    color="error"
                    fontWeight={500}
                  >
                    No associated records found.
                  </Typography>
                </Stack>
              )}

              {row.childs.map((row, index) => {
                return (
                  <Stack
                    direction={"row"}
                    spacing={0}
                    sx={{
                      borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    }}
                  >
                    {hasDetailColumns && (
                      <DataTableV2Cell
                        renderColumnIn="row"
                        width="80px"
                        masterColumnResize={masterColumnResize}
                        minColumnWidth={minColWidth}
                      />
                    )}
                    {selectionMode !== "none" && (
                      <DataTableV2Cell
                        renderColumnIn="row"
                        width="80px"
                        masterColumnResize={masterColumnResize}
                        minColumnWidth={minColWidth}
                      />
                    )}
                    {masterColumns.map((masterColumn) => {
                      if (!masterColumn) return null;
                      const detailColumnIndex = detailColumns.findIndex(
                        (x) => x.masterColumnKeyName === masterColumn.key,
                      );
                      if (detailColumnIndex > -1) {
                        return (
                          <DataTableV2Cell
                            renderColumnIn="row"
                            width={masterColumn.width}
                            masterColumnResize={masterColumnResize}
                            masterColumnKeyName={masterColumn.key}
                            minColumnWidth={minColWidth}
                            autoColumnWidth={autoColumnWidths[masterColumn.key]}
                          >
                            {onTableDetailRowCellRender(
                              row,
                              detailColumnIndex,
                              index,
                            )}
                          </DataTableV2Cell>
                        );
                      }
                      return (
                        <DataTableV2Cell
                          renderColumnIn="row"
                          width={masterColumn.width}
                          masterColumnResize={masterColumnResize}
                          masterColumnKeyName={masterColumn.key}
                          minColumnWidth={minColWidth}
                          autoColumnWidth={autoColumnWidths[masterColumn.key]}
                        ></DataTableV2Cell>
                      );
                    })}
                  </Stack>
                );
              })}
            </>
          </Collapse>
        </Stack>
      )}
    </React.Fragment>
  );
};

const DataTableV2Cell: React.FC<{
  width?: string;
  children?: React.ReactNode;
  isFirstColumnSticky?: boolean;
  isLastColumnSticky?: boolean;
  renderColumnIn: "row" | "header";
  sx?: SxProps<Theme>;
  masterColumnResize?: IDataTableV2MasterColumnResize;
  masterColumnKeyName?: string;
  disableResize?: boolean;
  minColumnWidth?: number;
  /** Per-column minimum from IDataTableV2MasterColumn.minWidth — takes precedence */
  columnMinWidth?: number;
  isLastColumn?: boolean;
  /** Row highlight background color — overrides the default white for row cells */
  rowHighlightColor?: string;
  /** Auto-computed width from estimateColumnWidth helper */
  autoColumnWidth?: number;
  /**
   * When true the cell lives inside the detached sticky-header row (which is
   * translated as a whole to track horizontal scroll). In that case we must NOT
   * apply position:sticky on the individual cell — the column is already
   * "pinned" because the header sits outside the scroll container.
   * Applying per-cell sticky here would cause the header cell to remain fixed
   * at left:0 while the translateX of the parent row shifts everything else,
   * making the header column appear to scroll away.
   * Instead, stickyMarker is used to counter-translate the cell back in JS.
   */
  isDetachedHeader?: boolean;
  /**
   * Set to "first" or "last" on sticky column cells inside the detached header
   * so the scroll-sync JS can counter-translate them back to their pinned edge.
   */
  stickyMarker?: "first" | "last";
}> = React.memo((props) => {
  const {
    children,
    isFirstColumnSticky,
    isLastColumnSticky,
    renderColumnIn,
    masterColumnResize,
    masterColumnKeyName,
    disableResize = false,
    minColumnWidth = 100,
    columnMinWidth,
    isLastColumn = false,
    rowHighlightColor,
    autoColumnWidth,
    isDetachedHeader = false,
    stickyMarker,
  } = props;

  // Per-column minWidth wins over the table-wide computed minimum
  const effectiveMin = columnMinWidth ?? minColumnWidth;
  const theme = useTheme();
  const [width, setWidth] = React.useState(() => {
    // Priority: cached resize > explicit width > auto-estimated > effectiveMin
    if (masterColumnResize && masterColumnKeyName && masterColumnResize.values?.[masterColumnKeyName]) {
      return masterColumnResize.values[masterColumnKeyName].width;
    }
    const baseWidth = removePx(props.width || "0px");
    if (baseWidth > 0) return baseWidth;
    // Use auto-estimated width if available, otherwise fall back to effectiveMin
    return autoColumnWidth || effectiveMin;
  });
  const columnRef = React.useRef<HTMLDivElement>(null);
  const dragWidthRef = React.useRef<number>(width);

  // Header-only: track whether user is actively dragging
  const [isDragging, setIsDragging] = React.useState(false);
  // Position (clientX) of the drag overlay line
  const [overlayX, setOverlayX] = React.useState(0);
  // Row cells: enable transition only after a resize is committed, not during drag
  const [enableTransition, setEnableTransition] = React.useState(false);

  const isHeader = renderColumnIn === "header";

  // Sync width when the cached/server resize value for this column arrives or
  // changes (e.g. saved widths loaded from get-user-meta-config AFTER mount).
  // Skip while the user is actively dragging to avoid fighting the drag.
  const cachedWidth =
    masterColumnResize && masterColumnKeyName
      ? masterColumnResize.values?.[masterColumnKeyName]?.width
      : undefined;
  React.useEffect(() => {
    if (isDragging) return;
    if (cachedWidth !== undefined && cachedWidth !== width) {
      setWidth(cachedWidth);
      dragWidthRef.current = cachedWidth;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedWidth]);

  const getInitialWidth = React.useCallback((): number => {
    if (props.width !== undefined) return removePx(props.width);
    if (columnRef.current) return columnRef.current.getBoundingClientRect().width;
    return 0;
  }, [props.width]);

  const onResizeStart = React.useCallback((event: any) => {
    setIsDragging(true);
    setEnableTransition(false);
    // Anchor the line to the cell's current right edge, not the raw mouse X
    if (columnRef.current) {
      const rect = columnRef.current.getBoundingClientRect();
      setOverlayX(rect.right);
    }
  }, []);

  // onResize: update local width + overlay — track cell right edge from width delta
  const onResize = React.useCallback((event: any, { size }: any) => {
    const newWidth = size.width > 0 ? size.width : getInitialWidth();
    dragWidthRef.current = newWidth;
    setWidth(newWidth);
    // Keep line at the cell's right edge: left offset + new width
    if (columnRef.current) {
      const rect = columnRef.current.getBoundingClientRect();
      const leftEdge = rect.left;
      setOverlayX(leftEdge + newWidth);
    }
  }, [getInitialWidth]);

  // onResizeStop: commit to parent + hide overlay + enable transition for row cells
  const onResizeStop = React.useCallback((_event: any, { size }: any) => {
    setIsDragging(false);
    setEnableTransition(true); // now row cells animate to the committed width
    const newWidth = dragWidthRef.current || size.width;
    if (isHeader && masterColumnResize && masterColumnKeyName) {
      const newValues = {
        ...(masterColumnResize.values || {}),
        [masterColumnKeyName]: { width: newWidth },
      };
      masterColumnResize.onResize(newValues);
    }
  }, [isHeader, masterColumnResize, masterColumnKeyName]);

  // Double-click resize handle: toggle between auto-fit and initial width for this column
  const isColumnAutoFittedRef = React.useRef(false);
  const onResizeHandleDoubleClick = React.useCallback(() => {
    if (!isHeader || !masterColumnResize || !masterColumnKeyName) return;

    if (isColumnAutoFittedRef.current) {
      // Shrink back to initial width — remove from resize values
      const newValues = { ...(masterColumnResize.values || {}) };
      delete newValues[masterColumnKeyName];
      masterColumnResize.onResize(newValues);
      isColumnAutoFittedRef.current = false;
    } else {
      // Expand: measure all visible cells via DOM
      const allCells = document.querySelectorAll(
        `[data-column-key="${masterColumnKeyName}"]`
      );

      let maxContentWidth = 0;
      allCells.forEach((cell) => {
        const inner = cell.firstElementChild as HTMLElement;
        if (inner) {
          const prevOverflow = inner.style.overflow;
          const prevWhiteSpace = inner.style.whiteSpace;
          inner.style.overflow = "visible";
          inner.style.whiteSpace = "nowrap";
          const contentWidth = inner.scrollWidth + 16;
          inner.style.overflow = prevOverflow;
          inner.style.whiteSpace = prevWhiteSpace;
          maxContentWidth = Math.max(maxContentWidth, contentWidth);
        }
      });

      const newWidth = Math.max(maxContentWidth, 30);

      setWidth(newWidth);
      dragWidthRef.current = newWidth;
      setEnableTransition(true);

      const newValues = {
        ...(masterColumnResize.values || {}),
        [masterColumnKeyName]: { width: newWidth },
      };
      masterColumnResize.onResize(newValues);
      isColumnAutoFittedRef.current = true;
    }
  }, [isHeader, masterColumnResize, masterColumnKeyName, effectiveMin]);

  // Header: measure DOM width if not already in masterColumnResize, then commit it
  // so all row cells share the same authoritative initial width
  React.useLayoutEffect(() => {
    if (!isHeader || !masterColumnResize || !masterColumnKeyName) return;

    // If already stored (from cache or previous resize), trust it
    if (masterColumnResize.values?.[masterColumnKeyName]) {
      const storedWidth = masterColumnResize.values[masterColumnKeyName].width;
      setWidth(storedWidth);
      dragWidthRef.current = storedWidth;
      return;
    }

    // Not stored — measure DOM and commit so row cells can read it
    const measured =
      props.width !== undefined
        ? removePx(props.width)
        : columnRef.current
          ? Math.round(columnRef.current.getBoundingClientRect().width)
          : 160;

    if (measured > 0) {
      setWidth(measured);
      dragWidthRef.current = measured;
      const newValues = {
        ...(masterColumnResize.values || {}),
        [masterColumnKeyName]: { width: measured },
      };
      masterColumnResize.onResize(newValues);
    }
    // Run once on mount — eslint-disable to avoid stale dep warnings
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Row cells: sync width whenever masterColumnResize.values changes (after resize stop)
  const prevResizeValueRef = React.useRef<number | undefined>(undefined);
  React.useLayoutEffect(() => {
    if (masterColumnResize && masterColumnKeyName) {
      if (masterColumnResize.values?.[masterColumnKeyName]) {
        // Sync to externally set width
        const newWidth = masterColumnResize.values[masterColumnKeyName].width;
        if (newWidth !== prevResizeValueRef.current) {
          prevResizeValueRef.current = newWidth;
          setWidth(newWidth);
          dragWidthRef.current = newWidth;
        }
      } else if (prevResizeValueRef.current !== undefined) {
        // Key was removed (reset) — fall back to autoColumnWidth or explicit width or effectiveMin
        prevResizeValueRef.current = undefined;
        const fallback = autoColumnWidth || removePx(props.width || "0px") || effectiveMin;
        setWidth(fallback);
        dragWidthRef.current = fallback;
      }
    }
  }, [masterColumnResize?.values, masterColumnKeyName]);

  const computedWidth = width || undefined;

  // Row cells use a CSS transition so the width change after stop is animated
  // Only enabled after drag stops — prevents the leftward-then-snap glitch during drag
  const transitionStyle = (renderColumnIn === "row" && enableTransition)
    ? "width 120ms ease-out, min-width 120ms ease-out, max-width 120ms ease-out"
    : undefined;

  const cellSx: SxProps<Theme> = React.useMemo(() => {
    // Use a single consistent width — no flex growth, no responsive min-width divergence
    const w = computedWidth ? `${computedWidth}px` : `${autoColumnWidth || effectiveMin}px`;
    const bg =
      theme.palette.mode === "dark"
        ? (renderColumnIn === "row" && rowHighlightColor ? rowHighlightColor : "rgb(31,41,55)")
        : renderColumnIn === "header"
          ? "#F3F4F6"
          : (rowHighlightColor || "#fff");

    const base: SxProps<Theme> = {
      background: bg,
      // Distribute widths proportionally across all columns to fill container evenly
      // flex: "1 1 0px",
      // width: w,
      // minWidth: w,

      flex: isLastColumn ? "1 1 auto" : "none",
      width: isLastColumn ? "auto" : w,
      minWidth: isLastColumn ? w : w,
      maxWidth: isLastColumn ? "unset" : w,
      minHeight: "40px",
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      borderRight: renderColumnIn === "header" && !isLastColumn ? `1px solid ${theme.palette.grey[300]}` : undefined,
      overflow: "hidden",
      boxSizing: "border-box",
      transition: transitionStyle,
      px: "0px",
    };

    if (isFirstColumnSticky || isLastColumnSticky) {
      // In a detached sticky header (outside the horizontal scroll container)
      // per-cell position:sticky must NOT be applied — the header stack as a
      // whole is already "pinned" horizontally via translateX tracking. Adding
      // sticky here would fight the transform and make the header cell appear
      // to scroll away during horizontal scroll.
      if (isDetachedHeader) {
        return {
          ...base,
          pl: "4px",
          // Box shadow preserved so it looks visually pinned.
          // No position/left/right — the JS counter-translateX handles pinning.
          // zIndex elevated so when the cell slides back to left:0 it layers
          // above its right neighbours.
          ...(isFirstColumnSticky || isLastColumnSticky
            ? {
                boxShadow: isFirstColumnSticky
                  ? "2px 0 5px -2px rgba(0, 0, 0, 0.15)"
                  : "-2px 0 5px -2px rgba(0, 0, 0, 0.15)",
                position: "relative" as const,
                zIndex: 4,
              }
            : {}),
        };
      }
      return {
        ...base,
        pl: "4px",
        position: "sticky",
        boxShadow: isFirstColumnSticky
          ? "2px 0 5px -2px rgba(0, 0, 0, 0.1)"
          : "-2px 0 5px -2px rgba(0, 0, 0, 0.1)",
        left: isFirstColumnSticky ? 0 : "",
        right: isLastColumnSticky ? 0 : "",
        zIndex: 2,
      };
    }

    return base;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedWidth, isFirstColumnSticky, isLastColumnSticky, isLastColumn, effectiveMin, renderColumnIn, theme, transitionStyle, rowHighlightColor, autoColumnWidth, isDetachedHeader, stickyMarker]);

  return (
    <>
      {/* Drag overlay — full-height vertical line that follows the cursor */}
      {isDragging && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: overlayX,
            width: "2px",
            height: "100vh",
            bgcolor: "primary.main",
            opacity: 0.5,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        />
      )}

      <Resizable
        height={0}
        width={width}
        minConstraints={[30, 0]}
        maxConstraints={[2000, 0]}
        resizeHandles={
          isHeader && masterColumnResize && !disableResize && !isLastColumn ? ["e"] : []
        }
        onResizeStart={onResizeStart}
        onResize={onResize}
        onResizeStop={onResizeStop}
        handle={
          isHeader && masterColumnResize && !disableResize ? (
            <Box
              className="react-resizable-handle react-resizable-handle-e"
              onDoubleClick={onResizeHandleDoubleClick}
              sx={{
                position: "absolute",
                right: "4px",
                top: "50%",
                transform: "translateY(-50%)",
                height: "60%",
                width: "8px",
                cursor: "col-resize",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 3,
                "&:hover .resize-grip, &:active .resize-grip": {
                  opacity: 1,
                  bgcolor: "primary.main",
                },
                ...(isDragging && {
                  "& .resize-grip": {
                    opacity: 1,
                    bgcolor: "primary.main",
                  },
                }),
              }}
            >
              {/* Single vertical grip — 3 stacked dots */}
              <Box
                className="resize-grip"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "3px",
                  opacity: 0.35,
                  transition: "opacity 0.15s, background 0.15s",
                }}
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: "3px",
                      height: "3px",
                      borderRadius: "50%",
                      bgcolor: isDragging ? "primary.main" : "grey.600",
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <span />
          )
        }
      >
        <Box
          ref={columnRef}
          sx={cellSx}
          data-column-key={masterColumnKeyName || ""}
          {...(stickyMarker === "first" ? { "data-sticky-first": "1" } : {})}
          {...(stickyMarker === "last" ? { "data-sticky-last": "1" } : {})}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              whiteSpace: "nowrap",
              overflow: "hidden",
              height: "100%",
              width: "100%",
              px: "4px",
            }}
          >
            {children}
          </Box>
        </Box>
      </Resizable>
    </>
  );
});